import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Alert, AtomButton, type IconDefinition, renderIcon } from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import type { ImportSource } from './useImportSources';

interface PickImportSourceModalProps {
    pickSource: (source: api.ImportContentSource) => void;
    sources: ImportSource[];
    onClose: () => void;
}

/**
 * Modal to import content into a new target space
 */
export function PickImportSourceModalNew(props: PickImportSourceModalProps): React.ReactElement {
    return <PickImportSourceModal {...props} />;
}

/**
 * Modal to import content into an existing target space
 */
export function PickImportSourceModalExisting(
    props: PickImportSourceModalProps & {
        space: api.Space;
    }
): React.ReactElement {
    const { space, onClose } = props;
    const { navigate } = useNavigation();

    const onPressGitSync = () => {
        onClose();

        navigate({
            screen: {
                type: 'space',
                organization: space.organization,
                space: space.id,
                sideSheet: {
                    type: 'gitsync-setup',
                },
            },
        });
    };

    return <PickImportSourceModal onPressGitSync={onPressGitSync} {...props} />;
}
/**
 * Modal to import content.
 */
function PickImportSourceModal(
    props: PickImportSourceModalProps & {
        onPressGitSync?: () => void;
    }
): React.ReactElement {
    const { sources, pickSource, onPressGitSync } = props;

    return (
        <>
            <div className="mb-6 flex flex-wrap justify-center gap-2">
                {sources.map(({ title, name, source, icon }) => (
                    <ImportSourceCard
                        key={title}
                        title={name}
                        onPress={() => pickSource(source)}
                        icon={icon}
                    />
                ))}
            </div>
            {onPressGitSync ? (
                <Alert
                    icon={Icon.GitHub}
                    kind="default"
                    title="Need to stay in sync?"
                    details={'Set up GitHub or GitLab Sync and link a repo for two-way syncing.'}
                    action={{
                        label: 'Set up now',
                        onPress: onPressGitSync,
                    }}
                />
            ) : null}
        </>
    );
}

/**
 * Card to pick a source to import.
 */
function ImportSourceCard(props: {
    title: string;
    description?: string;
    icon: IconDefinition;
    onPress: () => void;
}): React.ReactElement {
    const { title, description, icon, onPress } = props;

    return (
        <AtomButton
            className="flex shrink-0 grow-0 basis-[calc(50%-0.5rem)] flex-row items-center gap-2 rounded-md border border-base border-solid p-2.5 transition-all hover:bg-muted"
            onPress={onPress}
        >
            <div className="flex aspect-square size-7 items-center justify-center rounded-md border bg-base">
                {renderIcon(icon, { className: 'h-4 aspect-auto' })}
            </div>
            <div className="flex flex-col">
                <div>{title}</div>
                <div className="text-muted">{description}</div>
            </div>
        </AtomButton>
    );
}
